/**
 * Monthly net worth snapshot — mirrors "Shared Networth - Insights" style workbook.
 * Persisted locally in the browser (localStorage); extend to API later if needed.
 */

export interface NetWorthSnapshot {
  id: string;
  /** ISO date YYYY-MM-DD */
  recordDate: string;

  sloganOfTheMonth?: string;
  description?: string;

  /** Lending / receivables style outstanding (asset in your sheet) */
  outstanding?: number;
  savings?: number;
  /** Optional: salary reference for the month */
  salary?: number;

  equityAmountInvested?: number;
  equityCurrentValue?: number;
  mfAmountInvested?: number;
  mfCurrentValue?: number;

  corporateFdBonds?: number;
  ppf?: number;
  bankFd?: number;

  /** Optional total Europe / other bucket in INR (if set, used instead of EU salary×rate + EU equity×rate). */
  europeIncome?: number;
  /** EU salary (foreign units); combined INR = (euSalary + euEquity) × euConversionRate when europeIncome is not set. */
  euSalary?: number;
  euEquity?: number;
  /** INR per 1 unit of EU salary and EU equity (same rate for both). */
  euConversionRate?: number;

  /** Crypto / stablecoin in INR (₹ equivalent). */
  cryptoUsdt?: number;
  /** Crypto in USD; INR leg = cryptoUsd × cryptoToInrRate (added to cryptoUsdt). */
  cryptoUsd?: number;
  cryptoToInrRate?: number;
  realEstate?: number;
  realEstateDescription?: string;

  goldGrams?: number;
  goldInrPerGram?: number;
  /** If set, overrides grams × rate */
  goldTotalInr?: number;

  /**
   * US stock (Assets US): value in USD × INR/USD = INR. Matches Excel columns Stock, conversionrate, Total.
   */
  usStockUsd?: number;
  /** INR per 1 USD (e.g. 92.98) */
  usStockConversionRate?: number;
  /** If set, overrides Stock × conversion rate */
  usStockTotalInr?: number;

  /** @deprecated Legacy single field; used only if US stock fields are empty */
  usAssetsTotalInr?: number;

  usdAmount?: number;
  usdToInrRate?: number;

  monthlyInterestIncome?: number;

  customerFdLiability?: number;

  /**
   * Money captured from capital partners that is held in trust by the operator.
   * Subtracted from net worth (it belongs to partners, not the user). On-demand
   * "fetch current" reads this from `GET /api/v1/capitals/dashboard`.
   */
  partnerCapitalLiability?: number;

  /** @deprecated Import only; no longer shown in UI */
  excelTotalAssets?: number;
  /** @deprecated Import only */
  monthlyGrowthRatePct?: number;
  monthlyInterestIncomePossible?: number;
  monthOnMonthChangePct?: number;

  /** Persisted net worth from API (gross − customer FD liability). */
  totalNetworth?: number;
}

export const NETWORTH_STORAGE_KEY = 'whatzneu_networth_snapshots_v1';

export function newSnapshotId(): string {
  return `nw_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function goldComputedTotal(s: NetWorthSnapshot): number {
  if (s.goldTotalInr != null && !Number.isNaN(s.goldTotalInr)) {
    return Number(s.goldTotalInr);
  }
  const g = s.goldGrams ?? 0;
  const r = s.goldInrPerGram ?? 0;
  if (g > 0 && r > 0) return g * r;
  return 0;
}

function num(n: number | undefined): number {
  if (n == null || Number.isNaN(n)) return 0;
  return Number(n);
}

/** USD holding converted to INR */
export function usdValueInr(s: NetWorthSnapshot): number {
  const a = num(s.usdAmount);
  const r = num(s.usdToInrRate);
  if (a > 0 && r > 0) return a * r;
  return 0;
}

/** US stock bucket in INR (Assets US). */
export function computeUsStockInr(s: NetWorthSnapshot): number {
  if (s.usStockTotalInr != null && !Number.isNaN(s.usStockTotalInr)) {
    return num(s.usStockTotalInr);
  }
  const usd = num(s.usStockUsd);
  const rate = num(s.usStockConversionRate);
  if (usd > 0 && rate > 0) return usd * rate;
  return 0;
}

/** US stocks in INR, or legacy usAssetsTotalInr if new fields unset */
export function usStockOrLegacyAssetsUs(s: NetWorthSnapshot): number {
  const v = computeUsStockInr(s);
  if (v > 0) return v;
  return num(s.usAssetsTotalInr);
}

export function europeIncomeInr(s: NetWorthSnapshot): number {
  if (num(s.europeIncome) > 0) return num(s.europeIncome);
  const rate = num(s.euConversionRate);
  if (rate <= 0) return 0;
  return num(s.euSalary) * rate + num(s.euEquity) * rate;
}

export function cryptoInr(s: NetWorthSnapshot): number {
  const usd = num(s.cryptoUsd);
  let rate = num(s.cryptoToInrRate);
  if (usd > 0 && rate <= 0) {
    rate = num(s.usStockConversionRate);
  }
  return num(s.cryptoUsdt) + usd * rate;
}

/** Sum of asset buckets (matches your sheet intent; tweak if a field is income-only). */
export function computeGrossAssets(s: NetWorthSnapshot): number {
  const gold = goldComputedTotal(s);
  return (
    num(s.outstanding) +
    num(s.savings) +
    num(s.equityCurrentValue) +
    num(s.mfCurrentValue) +
    num(s.corporateFdBonds) +
    num(s.ppf) +
    num(s.bankFd) +
    europeIncomeInr(s) +
    cryptoInr(s) +
    num(s.realEstate) +
    gold +
    usStockOrLegacyAssetsUs(s) +
    usdValueInr(s)
  );
}

export function computeNetWorth(s: NetWorthSnapshot): number {
  return (
    computeGrossAssets(s) -
    num(s.customerFdLiability) -
    num(s.partnerCapitalLiability)
  );
}

export function assetBreakdown(s: NetWorthSnapshot): { name: string; value: number }[] {
  const entries: { name: string; value: number }[] = [
    { name: 'Outstanding', value: num(s.outstanding) },
    { name: 'Savings', value: num(s.savings) },
    { name: 'Equity (MV)', value: num(s.equityCurrentValue) },
    { name: 'MF / SIP (MV)', value: num(s.mfCurrentValue) },
    { name: 'Corp FD / Bonds', value: num(s.corporateFdBonds) },
    { name: 'PPF', value: num(s.ppf) },
    { name: 'Bank FD', value: num(s.bankFd) },
    { name: 'Europe / other (INR)', value: europeIncomeInr(s) },
    { name: 'Crypto (INR)', value: cryptoInr(s) },
    { name: 'Real estate', value: num(s.realEstate) },
    { name: 'Gold', value: goldComputedTotal(s) },
    { name: 'US stocks (INR)', value: usStockOrLegacyAssetsUs(s) },
    { name: 'USD (INR)', value: usdValueInr(s) },
  ];
  return entries.filter((e) => e.value > 0);
}
